import { useState, useCallback, useEffect } from "react";

// ============================================================================
// Types
// ============================================================================

export interface KaliArticleData {
  source: "kali";
  articleId: string;
  num: string | null;
  titre: string | null;
  etat: string | null;
  isActive: boolean;
  dateDebut: string | null;
  dateFin: string | null;
  idcc: string | null;
  conteneurId: string | null;
  conventionTitre: string | null;
  contentMarkdown: string | null;
  legifranceUrl: string;
}

export interface LegiArticleData {
  source: "legi";
  articleId: string;
  num: string | null;
  etat: string | null;
  isActive: boolean;
  dateDebut: string | null;
  dateFin: string | null;
  texteId: string | null;
  articleType: string | null;
  nota: string | null;
  ancestryPath: Array<{ id: string; titre: string }>;
  contentMarkdown: string | null;
  legifranceUrl: string;
}

export interface SectionData {
  type: "section";
  sectionId: string;
  titre: string | null;
  parentSectionId: string | null;
  texteId: string | null;
  ancestryPath: Array<{ id: string; titre: string }>;
  childSections: Array<{ id: string; titre: string }>;
  childArticles: Array<{ id: string; num: string; contentMarkdown: string | null }>;
  legifranceUrl: string;
}

export type ArticleData = KaliArticleData | LegiArticleData;
export type ExplorerItem = (ArticleData & { type: "article" }) | SectionData;

interface HistoryEntry {
  type: "article" | "section";
  id: string;
}

export interface LawExplorerState {
  currentItem: ExplorerItem | null;
  history: HistoryEntry[];
  isLoading: boolean;
  error: string | null;
}

// ============================================================================
// Hook
// ============================================================================

export function useLawExplorer(articleId: string | null) {
  const [state, setState] = useState<LawExplorerState>({
    currentItem: null,
    history: [],
    isLoading: false,
    error: null,
  });

  // Define fetch functions BEFORE the useEffect that uses them
  const fetchArticle = useCallback(async (id: string) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await fetch(`/api/preview/article/${encodeURIComponent(id)}`);
      if (!response.ok) {
        throw new Error("Article non trouve");
      }
      const data: ArticleData = await response.json();

      setState((prev) => ({
        ...prev,
        currentItem: { ...data, type: "article" as const },
        isLoading: false,
        error: null,
      }));
    } catch (err) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : "Erreur inconnue",
      }));
    }
  }, []);

  const fetchSection = useCallback(async (id: string) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await fetch(`/api/preview/section/${encodeURIComponent(id)}`);
      if (!response.ok) {
        throw new Error("Section non trouvee");
      }
      const data = await response.json();

      setState((prev) => ({
        ...prev,
        currentItem: { ...data, type: "section" } as SectionData,
        isLoading: false,
        error: null,
      }));
    } catch (err) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : "Erreur inconnue",
      }));
    }
  }, []);

  // Fetch article data when articleId changes
  useEffect(() => {
    if (!articleId) {
      setState({
        currentItem: null,
        history: [],
        isLoading: false,
        error: null,
      });
      return;
    }

    fetchArticle(articleId);
  }, [articleId, fetchArticle]);

  const navigateTo = useCallback(
    (type: "article" | "section", id: string) => {
      // Save current item to history before navigating
      if (state.currentItem) {
        const currentEntry: HistoryEntry =
          state.currentItem.type === "article"
            ? { type: "article", id: state.currentItem.articleId }
            : { type: "section", id: state.currentItem.sectionId };

        setState((prev) => ({
          ...prev,
          history: [...prev.history, currentEntry],
        }));
      }

      if (type === "article") {
        fetchArticle(id);
      } else {
        fetchSection(id);
      }
    },
    [state.currentItem, fetchArticle, fetchSection],
  );

  const goBack = useCallback(() => {
    if (state.history.length === 0) return;

    const newHistory = [...state.history];
    const previousEntry = newHistory.pop()!;

    setState((prev) => ({ ...prev, history: newHistory }));

    if (previousEntry.type === "article") {
      fetchArticle(previousEntry.id);
    } else {
      fetchSection(previousEntry.id);
    }
  }, [state.history, fetchArticle, fetchSection]);

  return {
    state,
    navigateTo,
    goBack,
    canGoBack: state.history.length > 0,
  };
}
